// Reusable animated-concept content library.
// Concepts here are language-agnostic — the same "Variables" story works
// for Python, Java, JavaScript, Lua, Perl, Go, etc.  A tiny code snippet
// per-language is looked up from CODE_EXAMPLES so learners can see the same
// idea in their language of choice.

// ---------- CONCEPTS ----------
// Each concept has:
//   id           — matched against chapter titles / tags
//   title        — heading shown at the top of the tab
//   character    — mood/pose passed to <ChapterCharacter />  (mentor|coder|scientist)
//   scenes[]     — { text, emoji, note?, tip? }  played one at a time
//   codeExamples — { python: '..', javascript: '..', ... }
//   summary      — the takeaway line at the end
export const CONCEPT_LIBRARY = {
  variables: {
    title: 'Variables — Boxes that Hold Your Data',
    character: 'mentor',
    scenes: [
      { emoji: '📦', text: "Imagine a shelf full of labelled boxes. Each box has a name (the variable) and something inside (the value)." },
      { emoji: '🖍️', text: "You write 'name = \"Aarav\"' — now the box labelled name holds the string \"Aarav\"." },
      { emoji: '🔁', text: "Later you can change what is inside: 'name = \"Priya\"'. The box stays, only its contents change." },
      { emoji: '💡', text: "This is how programs remember things — every score, every score-multiplier, every user is a variable somewhere.", tip: "Prefer names like student_age over x. Six months later you will thank yourself." },
    ],
    summary: 'A variable = name + value. Give it a clear name, put data inside, read or update it later.',
  },
  loops: {
    title: 'Loops — Ask the Computer to Repeat',
    character: 'coder',
    scenes: [
      { emoji: '🔁', text: "You have 30 exam papers to grade. You could write the same code 30 times… or use a loop." },
      { emoji: '➰', text: "'for paper in papers: grade(paper)' — one line, runs 30 times, once per paper." },
      { emoji: '⏱️', text: "'while battery > 0:' keeps running until the condition becomes false. Perfect for games, timers, servers." },
      { emoji: '🎯', text: "The loop body is indented (Python) or wrapped in braces (JS/Java). That indent tells the computer 'this is inside the loop'." , tip: "Always make sure a while-loop has a way to stop, otherwise the CPU spins forever." },
    ],
    summary: 'Loops let one instruction handle a whole collection — cleaner code, fewer bugs, faster to change.',
  },
  conditionals: {
    title: 'If / Else — Teach the Computer to Choose',
    character: 'mentor',
    scenes: [
      { emoji: '🚦', text: "A traffic light is a conditional in real life. Green? Go. Red? Stop. Yellow? Slow down." },
      { emoji: '❓', text: "'if score >= 40: print(\"Pass\")' — the code inside only runs when the condition is true." },
      { emoji: '🔀', text: "Add 'else:' for the other case. Add 'elif' (or else-if) to check more possibilities in order." },
      { emoji: '🧠', text: "Use == to compare, and =  to assign. Mixing them up is the #1 beginner bug in every language.", tip: "Order matters: elif branches are checked top-to-bottom. Put the tightest condition first." },
    ],
    summary: 'Conditions branch your code. if / elif / else lets one program behave differently for different inputs.',
  },
  functions: {
    title: 'Functions — Package a Recipe Once, Use It Forever',
    character: 'scientist',
    scenes: [
      { emoji: '📖', text: "A function is a mini recipe with a name. You define the steps once, then re-use it whenever you need that outcome." },
      { emoji: '🧾', text: "Inputs go in through parameters. The result comes out via 'return'." },
      { emoji: '🧩', text: "Well-named functions read like a story: 'calculate_gst(price)' explains itself. No comments needed." },
      { emoji: '🔒', text: "Variables created inside a function stay inside — that's called scope, and it saves you from name clashes.", tip: "One function = one job. If your function has three 'and's in its name, split it." },
    ],
    summary: 'Functions turn a big program into small, testable, reusable building blocks.',
  },
  data_types: {
    title: 'Data Types — Why Numbers, Strings & Lists Behave Differently',
    character: 'mentor',
    scenes: [
      { emoji: '🔢', text: "Integers count things: 42, -7, 0. You can add, subtract, multiply." },
      { emoji: '🧵', text: "Strings are text: \"Hello\", \"नमस्ते\". You can join them with '+'." },
      { emoji: '📚', text: "Lists (a.k.a. arrays) hold ordered items: [10, 20, 30]. Use an index to reach into them." },
      { emoji: '🗺️', text: "Dictionaries / maps store key → value pairs: {\"name\": \"Aarav\", \"score\": 91}." , tip: "The wrong type is the most common runtime crash. print(type(x)) is your best friend when debugging." },
    ],
    summary: 'Every value has a type. Pick the type that matches the shape of your data.',
  },
  oop: {
    title: 'Classes & Objects — Bundle Data With Behavior',
    character: 'coder',
    scenes: [
      { emoji: '🏗️', text: "A class is a blueprint. An object is a house built from that blueprint." },
      { emoji: '🚗', text: "'class Car:' with attributes like colour, speed and methods like accelerate() bundles data and behaviour together." },
      { emoji: '👨‍👩‍👧', text: "Inheritance lets ElectricCar re-use Car's engine of logic and only add what is new (charge_battery)." },
      { emoji: '🔐', text: "Encapsulation hides internal details behind clean methods — outside code never touches the internals directly.", tip: "Start with plain functions. Reach for a class only when several functions clearly share the same data." },
    ],
    summary: 'OOP models the real world: things (objects) that know their own data (attributes) and how to act (methods).',
  },
  debugging: {
    title: 'Debugging — Reading What the Computer Is Actually Doing',
    character: 'scientist',
    scenes: [
      { emoji: '🐛', text: "A bug is a difference between what you expected and what actually happened. Every bug is fixable." },
      { emoji: '🖨️', text: "Print the value just before the crash. 9 times out of 10 the mistake is now obvious." },
      { emoji: '🐾', text: "Set a breakpoint (click the gutter in this IDE) and step line by line to see how variables change." },
      { emoji: '📝', text: "Read the error message TOP-TO-BOTTOM. The last line usually names the exact line of code that broke.", tip: "Change ONE thing at a time. If you change three things and the bug disappears, you learned nothing." },
    ],
    summary: 'Debugging is a science: observe → hypothesis → change → verify. Slow down, look at the data.',
  },
  setup: {
    title: 'Setting Up Your Machine — Before You Write Any Code',
    character: 'mentor',
    scenes: [
      { emoji: '⬇️', text: "Every language has an official installer. Grab it only from the official site — never a random blog." },
      { emoji: '🛠️', text: "After installing, close and reopen your terminal, then check the version. If it prints, you're ready." },
      { emoji: '📝', text: "Install VS Code as your editor. Add the extension for your language for autocomplete + linting." },
      { emoji: '🚀', text: "Create a fresh folder for each project. Never work directly on your Desktop.", tip: "'PATH not found' errors mean the installer's tick-box for 'Add to PATH' was not selected. Re-run the installer." },
    ],
    summary: 'A clean, correct setup saves you hours of confusion later. Invest 10 minutes now.',
  },
};

// ---------- CODE SNIPPETS PER CONCEPT + LANGUAGE ----------
// Only the smallest possible example — the concept tab is about *ideas*,
// not about copy-pasting long code.  The main editor is where students code.
export const CODE_EXAMPLES = {
  variables: {
    python: `name = "Aarav"\nscore = 91\nprint(name, score)`,
    javascript: `let name = "Aarav";\nlet score = 91;\nconsole.log(name, score);`,
    java: `String name = "Aarav";\nint score = 91;\nSystem.out.println(name + " " + score);`,
    c: `char name[] = "Aarav";\nint score = 91;\nprintf("%s %d\\n", name, score);`,
    cpp: `std::string name = "Aarav";\nint score = 91;\nstd::cout << name << " " << score;`,
    go: `name := "Aarav"\nscore := 91\nfmt.Println(name, score)`,
    lua: `local name = "Aarav"\nlocal score = 91\nprint(name, score)`,
    perl: `my $name = "Aarav";\nmy $score = 91;\nprint "$name $score\\n";`,
    php: `$name = "Aarav";\n$score = 91;\necho "$name $score";`,
  },
  loops: {
    python: `for i in range(5):\n  print("Step", i)`,
    javascript: `for (let i = 0; i < 5; i++) {\n  console.log("Step", i);\n}`,
    java: `for (int i = 0; i < 5; i++) {\n  System.out.println("Step " + i);\n}`,
    c: `for (int i = 0; i < 5; i++) {\n  printf("Step %d\\n", i);\n}`,
    cpp: `for (int i = 0; i < 5; i++) {\n  std::cout << "Step " << i << "\\n";\n}`,
    go: `for i := 0; i < 5; i++ {\n  fmt.Println("Step", i)\n}`,
    lua: `for i = 0, 4 do\n  print("Step", i)\nend`,
    perl: `for my $i (0..4) {\n  print "Step $i\\n";\n}`,
    php: `for ($i = 0; $i < 5; $i++) {\n  echo "Step $i\\n";\n}`,
  },
  conditionals: {
    python: `score = 72\nif score >= 40:\n  print("Pass")\nelse:\n  print("Retry")`,
    javascript: `let score = 72;\nif (score >= 40) console.log("Pass");\nelse console.log("Retry");`,
    java: `int score = 72;\nif (score >= 40) System.out.println("Pass");\nelse System.out.println("Retry");`,
    c: `int score = 72;\nif (score >= 40) printf("Pass\\n");\nelse printf("Retry\\n");`,
    cpp: `int score = 72;\nif (score >= 40) std::cout << "Pass";\nelse std::cout << "Retry";`,
    go: `score := 72\nif score >= 40 { fmt.Println("Pass") } else { fmt.Println("Retry") }`,
    lua: `local score = 72\nif score >= 40 then print("Pass") else print("Retry") end`,
    perl: `my $score = 72;\nif ($score >= 40) { print "Pass\\n" } else { print "Retry\\n" }`,
    php: `$score = 72;\nif ($score >= 40) echo "Pass"; else echo "Retry";`,
  },
  functions: {
    python: `def greet(name):\n  return "Namaste, " + name\n\nprint(greet("Priya"))`,
    javascript: `function greet(name) {\n  return "Namaste, " + name;\n}\nconsole.log(greet("Priya"));`,
    java: `static String greet(String name) {\n  return "Namaste, " + name;\n}`,
    c: `const char* greet(const char* n) { return n; }`,
    cpp: `std::string greet(std::string n) { return "Namaste, " + n; }`,
    go: `func greet(name string) string { return "Namaste, " + name }`,
    lua: `function greet(name) return "Namaste, " .. name end\nprint(greet("Priya"))`,
    perl: `sub greet { return "Namaste, " . $_[0]; }\nprint greet("Priya"), "\\n";`,
    php: `function greet($name) { return "Namaste, " . $name; }\necho greet("Priya");`,
  },
  data_types: {
    python: `age = 15\nname = "Priya"\nmarks = [82, 91, 76]\nstudent = {"name": name, "age": age}`,
    javascript: `let age = 15;\nlet name = "Priya";\nlet marks = [82, 91, 76];\nlet student = { name, age };`,
    java: `int age = 15;\nString name = "Priya";\nint[] marks = {82, 91, 76};`,
    c: `int age = 15;\nchar name[] = "Priya";\nint marks[] = {82, 91, 76};`,
    cpp: `int age = 15;\nstd::string name = "Priya";\nstd::vector<int> marks = {82, 91, 76};`,
    go: `age := 15\nname := "Priya"\nmarks := []int{82, 91, 76}`,
    lua: `local age = 15\nlocal name = "Priya"\nlocal marks = {82, 91, 76}`,
    perl: `my $age = 15;\nmy $name = "Priya";\nmy @marks = (82, 91, 76);`,
    php: `$age = 15;\n$name = "Priya";\n$marks = [82, 91, 76];`,
  },
  oop: {
    python: `class Car:\n  def __init__(self, colour):\n    self.colour = colour\n  def start(self):\n    print(self.colour, "car starts")\n\nCar("red").start()`,
    javascript: `class Car {\n  constructor(colour){ this.colour = colour; }\n  start(){ console.log(this.colour, "car starts"); }\n}\nnew Car("red").start();`,
    java: `class Car {\n  String colour;\n  Car(String c){ colour = c; }\n  void start(){ System.out.println(colour + " car starts"); }\n}`,
    cpp: `class Car {\npublic:\n  std::string colour;\n  Car(std::string c): colour(c){}\n  void start(){ std::cout << colour << " car starts"; }\n};`,
    go: `type Car struct{ colour string }\nfunc (c Car) start(){ fmt.Println(c.colour, "car starts") }`,
    lua: `Car = {}\nCar.__index = Car\nfunction Car.new(c) return setmetatable({colour=c}, Car) end\nfunction Car:start() print(self.colour.." car starts") end`,
    perl: `package Car;\nsub new { my ($c,%a)=@_; bless {%a},$c }\nsub start { print $_[0]->{colour}." car starts\\n" }`,
    php: `class Car {\n  public $colour;\n  function __construct($c){ $this->colour = $c; }\n  function start(){ echo $this->colour . " car starts"; }\n}\n(new Car("red"))->start();`,
  },
  debugging: {
    python: `x = 5\nprint("before:", x)\nx = x * 2\nprint("after:", x)`,
    javascript: `let x = 5;\nconsole.log("before:", x);\nx *= 2;\nconsole.log("after:", x);`,
    java: `int x = 5;\nSystem.out.println("before: " + x);\nx *= 2;\nSystem.out.println("after: " + x);`,
    c: `int x = 5;\nprintf("before: %d\\n", x);\nx *= 2;\nprintf("after: %d\\n", x);`,
    cpp: `int x = 5;\nstd::cout << "before " << x << "\\n";\nx *= 2;\nstd::cout << "after " << x;`,
    go: `x := 5\nfmt.Println("before:", x)\nx *= 2\nfmt.Println("after:", x)`,
    lua: `local x = 5\nprint("before:", x)\nx = x * 2\nprint("after:", x)`,
    perl: `my $x = 5;\nprint "before: $x\\n";\n$x *= 2;\nprint "after: $x\\n";`,
    php: `$x = 5;\necho "before: $x\\n";\n$x *= 2;\necho "after: $x";`,
  },
  setup: {
    python: `# Once installed, verify:\n#   python --version\nprint("hello!")`,
    javascript: `// Once installed, verify:\n//   node --version\nconsole.log("hello!");`,
    java: `// Verify: java --version\npublic class Main {\n  public static void main(String[] a){ System.out.println("hello!"); }\n}`,
    c: `// Verify: gcc --version\n#include <stdio.h>\nint main(){ printf("hello!"); }`,
    cpp: `// Verify: g++ --version\n#include <iostream>\nint main(){ std::cout << "hello!"; }`,
    go: `// Verify: go version\npackage main\nimport "fmt"\nfunc main(){ fmt.Println("hello!") }`,
    lua: `-- Verify: lua -v\nprint("hello!")`,
    perl: `# Verify: perl -v\nprint "hello!\\n";`,
    php: `<?php // Verify: php --version\necho "hello!";`,
  },
};

// ---------- MATCH A CHAPTER TITLE → CONCEPT ID ----------
// Simple keyword matcher — resilient enough for our chapter naming style.
export function conceptForChapter(chapter, chapterIndex) {
  const t = (chapter?.title || '').toLowerCase();
  if (chapterIndex === 0) return 'setup';
  if (/variable|declar|assign/.test(t)) return 'variables';
  if (/loop|for |while|iterat|repeat/.test(t)) return 'loops';
  if (/if|else|condition|branch|switch|decision/.test(t)) return 'conditionals';
  if (/function|method|procedure|def |routine/.test(t)) return 'functions';
  if (/type|string|list|array|dict|map|object types|primitive|number/.test(t)) return 'data_types';
  if (/class|object|oop|inherit|encapsul/.test(t)) return 'oop';
  if (/debug|error|exception|troubleshoot|breakpoint/.test(t)) return 'debugging';
  if (/setup|install|environment|getting started|introduction/.test(t)) return 'setup';
  return 'variables'; // safe default
}
